<template>
  <component
    :is="(href || to) && !disabled ? NuxtLink : 'button'"
    ref="el"
    class="dropdown__item group text-body-default hover:bg-state-bgr-hover hover:text-state-fgr-hover focus-within:bg-state-bgr-hover focus-within:text-state-fgr-hover flex min-h-7 w-full items-center justify-start gap-2.5 rounded-lg px-1.5 whitespace-nowrap transition-all"
    :tabindex="disabled ? -1 : 0"
    :class="{
      'cursor-not-allowed hover:bg-inherit!': disabled,
      'bg-state-bgr-active text-state-fgr-hover': active,
      'cursor-pointer': !disabled,
    }"
    :to="to || href"
    :external="!!href"
    :target="props.target"
    @click="callParentClose"
  >
    <slot name="prefix" />
    <slot name="custom" />
    <p class="flex w-full flex-col">
      <Tooltip :text="truncatedText" :disabled="!truncatedText">
        <span
          ref="titleEl"
          class="block truncate text-start"
          :class="{
            'text-fgr-critical': variant === 'destructive',
            '!text-state-fgr-disabled': disabled,
          }"
        >
          <slot>
            {{ title }}
          </slot>
        </span>
      </Tooltip>

      <slot name="subtext">
        <span v-if="subtext" class="text-body-small group-hover: text-start">{{ subtext }}</span>
      </slot>
    </p>
    <slot name="suffix" />
    <Icon v-if="selected" class="ml-auto" src="check" />
    <Icon v-if="hasArrow" class="ml-auto" src="arrow_right" />
  </component>
</template>

<script setup lang="ts">
  import { NuxtLink } from "#components";

  defineOptions({
    name: "DropdownItem",
  });

  const props = withDefaults(
    defineProps<{
      active?: boolean;
      closeOnClick?: boolean;
      disabled?: boolean;
      hasArrow?: boolean;
      href?: string;
      selected?: boolean;
      subtext?: string;
      target?: string;
      title?: string;
      to?: Record<string, any>;
      variant?: "default" | "destructive";
    }>(),
    {
      active: false,
      closeOnClick: true,
      disabled: false,
      hasArrow: false,
      href: undefined,
      selected: false,
      subtext: undefined,
      target: "_self",
      title: undefined,
      to: undefined,
      variant: "default",
    }
  );
  const instance = ref(null);
  const emit = defineEmits(["click"]);

  const el = ref<HTMLElement | null>(null);
  const titleEl = useTemplateRef("titleEl");
  const truncatedText = ref("");

  // Only show the full title as a tooltip when the ellipsis is actually hiding part of it.
  // Measured up front (not on hover) so the tooltip is already enabled when the pointer arrives.
  // Slotted content (e.g. PageHeader / table row actions) can bring its own ellipsis element, so check those too.
  const checkTruncated = () => {
    const span = titleEl.value;
    if (!span) return;
    const truncated = [span, ...span.querySelectorAll("*")].find((node) => node.scrollWidth > node.clientWidth);
    truncatedText.value = truncated?.textContent?.trim() ?? "";
  };
  const resizeObserver = import.meta.client ? new ResizeObserver(checkTruncated) : null;
  onMounted(() => titleEl.value && resizeObserver?.observe(titleEl.value));
  onBeforeUnmount(() => resizeObserver?.disconnect());
  onMounted(() => {
    instance.value = getCurrentInstance();
  });

  const callParentClose = (evt) => {
    if (props.disabled) return;
    if (!props.closeOnClick) {
      emit("click", evt);
      return;
    }

    const dropdownParent = getParentUntilHasEmitOptions(instance.value);

    emit("click", evt);

    nextTick(() => {
      if (dropdownParent) dropdownParent.emit("update:open", false);
    });
  };

  const getParentUntilHasEmitOptions = (instance: any): any => {
    if (instance) {
      if (instance?.emitsOptions && Object.prototype.hasOwnProperty.call(instance?.emitsOptions, "update:open"))
        return instance;
      else if (instance.parent) return getParentUntilHasEmitOptions(instance.parent);
    }
    return null;
  };

  defineExpose({
    el,
  });
</script>

<style>
  .dropdown__item.animate-right {
    animation: slide-in-right cubic-bezier(0.65, 0, 0.35, 1) forwards;
  }

  .dropdown__item.animate-left {
    animation: slide-in-left cubic-bezier(0.65, 0, 0.35, 1) forwards;
  }

  @keyframes slide-in-right {
    0% {
      transform: translateX(100%);
    }

    25% {
      transform: translateX(0%);
    }

    66% {
      transform: translateX(-5px);
    }

    100% {
      transform: translateX(0%);
    }
  }

  @keyframes slide-in-left {
    0% {
      transform: translateX(-100%);
    }

    25% {
      transform: translateX(0%);
    }

    66% {
      transform: translateX(-5px);
    }

    100% {
      transform: translateX(0%);
    }
  }
</style>
